import { useHead } from "@unhead/react"
import { brandName, siteUrl } from "@/data/site"

export function pageCanonical(path: string) {
  if (!path || path === "/") {
    return `${siteUrl}/`
  }

  const normalized = path.startsWith("/") ? path : `/${path}`
  const withSlash = normalized.endsWith("/") ? normalized : `${normalized}/`
  return `${siteUrl}${withSlash}`
}

export function useCanonical(path: string) {
  const href = pageCanonical(path)

  useHead({
    link: [
      {
        key: "canonical",
        rel: "canonical",
        href,
      },
    ],
  })
}

const localBusinessProvider = {
  "@type": "LocalBusiness",
  name: brandName,
  address: {
    "@type": "PostalAddress",
    streetAddress: "19 rue Louis Le Moenic",
    addressLocality: "Inguiniel",
    postalCode: "56240",
    addressRegion: "Morbihan",
    addressCountry: "FR",
  },
}

const areaServed = [
  { "@type": "City", name: "Plouay" },
  { "@type": "City", name: "Lorient" },
  { "@type": "AdministrativeArea", name: "Morbihan" },
]

export type JsonLdOfferInput = {
  label: string
  price: string
}

export type JsonLdServiceInput = {
  name: string
  description?: string
  url: string
  image?: string
  serviceType?: string
  offers: JsonLdOfferInput[]
}

function parseEuroAmount(price: string) {
  const match = price.replace(",", ".").match(/(\d+(?:\.\d+)?)/)
  return match?.[1]
}

function compactText(value?: string) {
  if (!value) return undefined
  const compact = value.replace(/\s+/g, " ").trim()
  return compact || undefined
}

export function buildServiceWithOffers(input: JsonLdServiceInput) {
  const offers = input.offers.map((offer) => {
    const price = parseEuroAmount(offer.price)
    return {
      "@type": "Offer",
      name: offer.label,
      ...(price ? { price, priceCurrency: "EUR" } : {}),
      availability: "https://schema.org/InStock",
      url: input.url,
    }
  })

  return {
    "@type": "Service",
    name: input.name,
    description: compactText(input.description),
    url: input.url,
    image: input.image,
    serviceType: input.serviceType,
    provider: localBusinessProvider,
    areaServed,
    offers: offers.length === 1 ? offers[0] : offers,
  }
}

export function jsonLdGraphScript(services: JsonLdServiceInput[], key: string) {
  return {
    key,
    type: "application/ld+json",
    innerHTML: JSON.stringify({
      "@context": "https://schema.org",
      "@graph": services.map(buildServiceWithOffers),
    }),
  }
}
